/**
 * "Call Lighthouse": a voice call over an ordinary agent session. Each spoken turn is a normal message
 * (`input_mode: 'voice'`) through the same `useAgentSession` the chat uses, so tools, approvals, questions,
 * browser tasks and the transcript work exactly as typed; the replies are read aloud as they stream.
 *
 *   tap ─► voiceCall.start() (sync: unlock audio) ─► callMachine (pure) ─► effects run here:
 *          STT (native recognizer | web mic + VAD + transcribe) · TTS (streamed PCM | device speech)
 *          session (send, cancel, approve) · agentSpeech (what to say) · haptics, earcons, wake lock, hold
 *
 * Two pipelines (picked per call from `GET /voice/config`, the classic one as the fallback):
 *   classic   STT → a turn → the agent's reply through TTS, with the server's talker (`voice.say`) filling the
 *             wait and words said mid-task steering the run (`steer`)
 *   realtime  an OpenAI speech-to-speech session over WebRTC is the voice; the backend hands requests to the
 *             agent and has the voice say its replies (realtime/connection.ts, backend app/harness/realtime.py)
 * Each turn's latency timeline (timing.ts) goes to `POST /voice/timing`.
 *
 * `voiceCall` is the app-wide API (start a call from any view; a native quick action can call it too) and
 * holds the reactive view the call UI and the chat header read. The engine behind it is created once by
 * `VoiceCallHost` (mounted in App.vue), because the session needs a component context; the call then
 * outlives navigation (the minimized pill keeps going).
 */
import { markRaw, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Capacitor } from '@capacitor/core'
import { agentApi, type VoiceConfig, type VoiceTalker } from '@/agent/api'
import { liveHeadline, turnSteps } from '@/agent/progress'
import { browserTaskFor, pendingApprovals, toolsForRun, type UiChangeset, type UiQuestion } from '@/agent/reducer'
import type { BrowserTaskCard } from '@/agent/protocol/types'
import { isSessionMounted, useAgentSession, type SessionOptions } from '@/agent/useAgentSession'
import { useHaptics } from '@/composables/useHaptics'
import useAssistantName from '@/composables/useAssistantName'
import { GUEST_MODE } from '@/guest/guestMode'
import { isDesktopShell, isNativePlatform, isTouchPrimary } from '@/utils/platform'
import { AgentSpeech } from './agentSpeech'
import { initialCallState, isLive, transition } from './callMachine'
import { audioContext, playEarcon, smoothLevel, unlockAudio } from './audio/context'
import { callStatusBar, keepAwake, notifyHaptic, onAppLifecycle } from './platform'
import { createNativeCallStt } from './stt/nativeCallStt'
import { nativeSpeech, nativeSpeechSupported } from './stt/nativeSpeech'
import { createStreamTts } from './tts/player'
import { connectRealtime, realtimeSupported, type RealtimeConnection } from './realtime/connection'
import { TurnTimeline } from './timing'
import { savedVoice } from './voices'
import { createFakeStt, createFakeTts, type FakeStt } from './dev/fakeEngines'
import type { CallEffect, CallErrorKind, CallEvent, CallState, EndReason, HapticKind, SttEngine, SttHandlers, TtsEngine } from './types'

export interface StartCallOptions {
    /** Continue this conversation; without one the call starts a new conversation. */
    sessionId?: string | null
    /** For a new conversation. */
    surface?: string
    entityRefs?: { type: string; id: string }[]
    title?: string
    /** A new conversation was created for the call: the host's chat can show it. */
    onSessionCreated?: (id: string) => void
    /** "Chat": show the conversation (default: route to it). The call minimizes first. */
    openChat?: (sessionId: string) => void
}

export interface CallCaptions {
    /** What the creator is saying / just said. */
    user: string
    /** The sentence Lighthouse is saying. */
    agent: string
    /** While it works: the step in plain words ("Checking your invoices"). */
    progress: string
}

const HOLD_LIMIT_MS = 120_000
const IDLE_PROMPT_MS = 90_000
const IDLE_END_MS = 120_000
const ENDED_LINGER_MS = 1600
/** How long the mic waits after the agent stops speaking, for the audio route to settle (see resumeMic). */
const MIC_AFTER_SPEECH_MS = 200
const DISCLOSED_KEY = 'lh:voice-ai-disclosed'
/** Phases where the mic should be open (unless muted). */
const HEARING: CallState['phase'][] = ['listening', 'userSpeaking', 'thinking', 'needsApproval']
/** How long a call waits for the server's voice config before going ahead without it (classic, no talker). */
const CONFIG_WAIT_MS = 1500
const NO_CONFIG: VoiceConfig = { talker: 'off', steer: false, realtime: false }

let voiceConfig: Promise<VoiceConfig> | null = null

/** What calls can use on this server (`GET /voice/config`), fetched once; older backends get the classic call. */
function loadVoiceConfig(): Promise<VoiceConfig> {
    if (!voiceConfig) {
        voiceConfig = (async () => ({ ...NO_CONFIG, ...(await agentApi().voiceConfig()) }))().catch(() => {
            voiceConfig = null // try again on the next call
            return NO_CONFIG
        })
    }
    return voiceConfig
}

/** Forget the fetched config (tests; the next call fetches it again). */
export function resetVoiceConfig(): void {
    voiceConfig = null
}

function within<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
    return Promise.race([p, new Promise<T>((r) => setTimeout(() => r(fallback), ms))])
}

/** Machine state (phase + flags), reactive, app-wide. */
const state = reactive<CallState>(initialCallState())

/** Everything else the call UI shows. */
const view = reactive({
    sessionId: null as string | null,
    /** This call's id (the chat's call line for it shows "live"). */
    callId: '',
    /** performance.now() when the call connected (0 before), and when it ended. */
    startedAt: 0,
    endedAt: 0,
    /** Ticks every second while a call is up (drives the timers). */
    now: 0,
    captions: { user: '', agent: '', progress: '' } as CallCaptions,
    /** Partials while the creator talks (native recognizers; the web path transcribes after). */
    liveCaptions: true,
    /** Speaker / receiver routing (not available to the WebView in v1). */
    speakerSupported: false,
    /** Show "AI voice" under the name (the first call on this device; always in the a11y label). */
    disclose: false,
    approval: null as UiChangeset | null,
    question: null as UiQuestion | null,
    browserTask: null as BrowserTaskCard | null,
    browserNeedsYou: false,
    /** A tap action in flight (approve / reject / answer). */
    busy: null as string | null,
    /** The creator hasn't spoken yet this call ("Go ahead, I'm listening"). */
    firstTurn: true,
    /** Bumped when a tapped answer fails to send (the question card resets its chips). */
    answerFailed: 0,
})

/** Mic and output levels, 0..1, smoothed every frame. Not reactive: the orb and pill read them in rAF. */
const levels = markRaw({ mic: 0, out: 0 })

interface Engine {
    start(opts: StartCallOptions): void
    dispatch(e: CallEvent): void
    approve(id: string, action: 'approve' | 'reject'): Promise<void>
    answer(text: string): Promise<void>
    openChat(): void
    say(text: string): void
}

let engine: Engine | null = null
/** A host is mounted (reactive, so call buttons appear once it is). */
const hostReady = ref(false)
let devFake = false

function liveNow(): boolean {
    return isLive(state.phase)
}

export function formatCallTime(ms: number): string {
    const s = Math.max(0, Math.floor(ms / 1000))
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    const sec = String(s % 60).padStart(2, '0')
    return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`
}

/** How long the call has been connected (frozen once it ends). */
export function callElapsed(): number {
    if (!view.startedAt) return 0
    return (view.endedAt || view.now) - view.startedAt
}

/** The app-wide call API. Safe to import anywhere; does nothing until a VoiceCallHost is mounted. */
export const voiceCall = {
    state: state as Readonly<CallState>,
    view,
    levels,
    /** Calls can work here (signed in, on a phone, a mic path exists, a host is mounted). Mobile only: desktop never offers calls. */
    available(): boolean {
        if (GUEST_MODE || !hostReady.value || !engine) return false
        if (devFake) return true
        if (!isTouchPrimary() || isDesktopShell()) return false
        if (isNativePlatform()) return nativeSpeechSupported()
        return typeof window !== 'undefined' && window.isSecureContext && typeof navigator?.mediaDevices?.getUserMedia === 'function'
            && typeof AudioWorkletNode !== 'undefined'
    },
    /**
     * Start (or return to) a call. Call it synchronously from the tap: audio is unlocked inside the gesture.
     * Returns false when calls aren't available.
     */
    /** Unlock audio inside a tap when the call itself starts a moment later (after creating a session). */
    prepare(): void {
        unlockAudio()
    },
    start(opts: StartCallOptions = {}): boolean {
        if (!engine || GUEST_MODE) return false
        if (!devFake && (!isTouchPrimary() || isDesktopShell())) return false
        if (liveNow()) {
            engine.dispatch({ type: 'EXPAND' })
            return true
        }
        unlockAudio()
        engine.start(opts)
        return true
    },
    end(reason: EndReason = 'user') {
        engine?.dispatch({ type: 'END', reason })
    },
    toggleMute() {
        engine?.dispatch({ type: 'TOGGLE_MUTE' })
    },
    setSpeaker(on: boolean) {
        engine?.dispatch({ type: 'SPEAKER', on })
    },
    /** The orb: interrupt while it speaks, send now while you talk. */
    tapOrb() {
        engine?.dispatch({ type: 'TAP_ORB' })
    },
    minimize() {
        engine?.dispatch({ type: 'MINIMIZE' })
    },
    expand() {
        engine?.dispatch({ type: 'EXPAND' })
    },
    /**
     * Something full-screen of this conversation came up (its live browser): a call on it that's expanded
     * gets out of the way (to the pill), so what the creator was told is "on your screen" really is.
     */
    yieldTo(sessionId: string | null | undefined) {
        if (sessionId && view.sessionId === sessionId && liveNow() && !state.flags.minimized) engine?.dispatch({ type: 'MINIMIZE' })
    },
    retry() {
        unlockAudio()
        engine?.dispatch({ type: 'RETRY' })
    },
    dismiss() {
        engine?.dispatch({ type: 'DISMISS' })
    },
    openChat() {
        engine?.openChat()
    },
    approve: (id: string) => engine?.approve(id, 'approve'),
    reject: (id: string) => engine?.approve(id, 'reject'),
    answer: (text: string) => engine?.answer(text),
    /** The chat header's green "0:42" chip while this conversation is on a call. */
    chipFor(sessionId: string | null | undefined): { active: boolean; label: string } | null {
        if (!sessionId || view.sessionId !== sessionId || !liveNow()) return null
        return { active: true, label: view.startedAt ? formatCallTime(callElapsed()) : 'Calling…' }
    },
}

export function useVoiceCall() {
    return voiceCall
}

/** Dev: drive calls with the fake speech engines (no mic, no speak endpoint). */
export function useFakeVoiceEngines(on = true): void {
    devFake = on
}

function haptic(kind: HapticKind, h: ReturnType<typeof useHaptics>) {
    if (kind === 'light' || kind === 'medium' || kind === 'heavy') void h.impact(kind)
    else if (kind === 'selection') void h.selectionChanged()
    else void notifyHaptic(kind)
}

function newCallId(): string {
    const rand = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID().slice(0, 8) : Math.random().toString(36).slice(2, 10)
    return `call-${Date.now().toString(36)}-${rand}`
}

/**
 * Create the engine. Call once from VoiceCallHost's setup (it needs the router and the component lifecycle
 * for the call's session). Returns false when another host already owns the call.
 */
export function createVoiceCall(): { owner: boolean; dispose: () => void } {
    if (engine) return { owner: false, dispose: () => {} }

    let router: ReturnType<typeof useRouter> | null = null
    try {
        router = useRouter()
    } catch {
        router = null
    }
    const haptics = useHaptics()
    const { assistantName } = useAssistantName()

    const sessionId = ref<string | null>(null)
    const sessionOpts: SessionOptions = {
        tier: 'quick',
        // The chat for this conversation (if it's on screen) runs ui.actions; the call only when it isn't.
        dispatchActions: () => !isSessionMounted(sessionId.value),
    }
    const session = useAgentSession(sessionId, sessionOpts)
    const st = session.state

    let opts: StartCallOptions = {}
    let stt: SttEngine | null = null
    let tts: TtsEngine | null = null
    let speech: AgentSpeech | null = null
    let callId = ''
    let sessionReady: Promise<string | null> = Promise.resolve(null)
    /** Resolves with the call's session id once it exists (no history load needed): the call lines use it. */
    let sessionIdReady: Promise<string | null> = Promise.resolve(null)
    /** Stops the current call's wait for the session's history (a new call or cleanup). */
    let stopHydrate: (() => void) | null = null
    let holdTimer: ReturnType<typeof setTimeout> | null = null
    let idleTimers: ReturnType<typeof setTimeout>[] = []
    let lingerTimer: ReturnType<typeof setTimeout> | null = null
    let clock: ReturnType<typeof setInterval> | null = null
    let raf = 0
    let micTarget = 0
    let lastFrame = 0
    let offLifecycle: (() => void) | null = null
    let sttRetry: ReturnType<typeof setTimeout> | null = null
    /** A mic resume held back until the audio route settles after the agent's voice (MIC_AFTER_SPEECH_MS). */
    let micResume: ReturnType<typeof setTimeout> | null = null
    /** performance.now() when the agent last stopped speaking. */
    let speechEndedAt = 0
    /** "Still there?" was said since the creator last spoke: the idle deadline doesn't move for it. */
    let idlePrompted = false
    /** Bumped per call, so a slow session load from an earlier call never attaches to this one. */
    let calls = 0
    const lastRunIds: string[] = []
    /** This call's pipeline and talker (decided at start from the server's config). */
    let pipeline: 'classic' | 'realtime' = 'classic'
    let talkerMode: VoiceTalker | null = null
    let configReady: Promise<VoiceConfig> = Promise.resolve(NO_CONFIG)
    let rt: RealtimeConnection | null = null
    /** The current spoken turn's latency timeline. */
    let turn: TurnTimeline | null = null

    // ── the machine ─────────────────────────────────────────────────────────

    // Events raised while effects run (speaking a line raises SPEAKABLE) wait for the current transition's
    // effects to finish, so they always apply in order.
    const pending: CallEvent[] = []
    let stepping = false

    function dispatch(e: CallEvent) {
        pending.push(e)
        if (stepping) return
        stepping = true
        try {
            while (pending.length) step(pending.shift()!)
        } finally {
            stepping = false
        }
    }

    function step(e: CallEvent) {
        const before = state.phase
        if (e.type === 'IDLE_PROMPT' && before === 'listening' && !state.runActive) idlePrompted = true
        const r = transition(state, e)
        // Before the effects run, so a mic resume in this same step sees that speech just ended.
        if (before === 'speaking' && r.state.phase !== 'speaking') speechEndedAt = performance.now()
        Object.assign(state, r.state)
        for (const fx of r.effects) run(fx)
        if (state.phase !== before) onPhase(before)
    }

    function onPhase(before: CallState['phase']) {
        // A turn of the creator's starts: clear the old captions.
        if (state.phase === 'userSpeaking') {
            view.captions.agent = ''
            view.firstTurn = false
        }
        if (state.phase === 'speaking') view.captions.user = ''
        if (state.phase === 'listening' && before !== 'connecting') view.captions.progress = ''
        // Only real activity (the creator speaking, a run working) restarts the idle clock, not the app's
        // own "Still there?" playing and draining.
        if (state.phase !== 'listening' && state.phase !== 'speaking') idlePrompted = false
        armIdle()
        if (state.phase === 'ended') {
            view.endedAt = performance.now()
            if (lingerTimer) clearTimeout(lingerTimer)
            lingerTimer = setTimeout(() => dispatch({ type: 'DISMISS' }), ENDED_LINGER_MS)
        }
    }

    /**
     * Reopen the mic, but not within MIC_AFTER_SPEECH_MS of the agent's voice stopping: on iOS the audio route is
     * still switching back from playback then, and starting the recognizer mid-switch fails (or, before the plugin
     * patch, crashed the app).
     */
    function resumeMic() {
        clearMicResume()
        const wait = MIC_AFTER_SPEECH_MS - (performance.now() - speechEndedAt)
        if (wait <= 0) {
            stt?.resume()
            return
        }
        micResume = setTimeout(() => {
            micResume = null
            stt?.resume()
        }, wait)
    }

    function clearMicResume() {
        if (micResume) clearTimeout(micResume)
        micResume = null
    }

    function run(fx: CallEffect) {
        switch (fx.type) {
            case 'stt':
                if (fx.op === 'start' && state.phase === 'connecting') {
                    void openMic()
                    return
                }
                if (rt) {
                    // Realtime: the mic stays open while the voice talks (it hears interruptions); only mute,
                    // hold and hang-up close it, and hang-up ends the call.
                    if (fx.op === 'stop' && (state.phase === 'ended' || state.phase === 'error')) closeRealtime()
                    else rt.setMuted(fx.op === 'pause' || fx.op === 'stop' || state.flags.muted)
                    return
                }
                if (fx.op === 'start') {
                    if (state.phase === 'connecting') void openMic()
                    else {
                        if (sttRetry) clearTimeout(sttRetry)
                        sttRetry = setTimeout(() => void restartMic(), state.sttFailures ? 500 : 0)
                    }
                } else if (fx.op === 'pause') {
                    clearMicResume()
                    stt?.pause()
                    micTarget = 0
                } else if (fx.op === 'resume') {
                    resumeMic()
                } else if (fx.op === 'finish') {
                    clearMicResume()
                    const engineNow = stt
                    const t = newTurn('classic')
                    void (engineNow ? engineNow.finish() : Promise.resolve('')).then((text) => {
                        if (text) view.captions.user = text
                        if (text) t.mark('transcript')
                        dispatch({ type: 'TRANSCRIPT', text })
                    })
                } else {
                    // Hold, hang-up, failure: a pending restart must not reopen the mic afterwards.
                    if (sttRetry) clearTimeout(sttRetry)
                    sttRetry = null
                    clearMicResume()
                    stt?.stop()
                    micTarget = 0
                }
                return
            case 'tts':
                if (fx.op === 'stop') {
                    tts?.stop()
                    rt?.interrupt()
                } else tts?.hold(fx.op === 'hold')
                return
            case 'send':
                void sendTurn(fx.text, fx.steer)
                return
            case 'cancelRun':
                void session.cancel().catch(() => {})
                return
            case 'replay':
                for (const t of speech?.replay() ?? []) speak(t, null)
                return
            case 'silenceRun':
                speech?.silenceCurrent()
                view.captions.agent = ''
                return
            case 'say':
                speak(fx.text, null)
                return
            case 'haptic':
                haptic(fx.kind, haptics)
                return
            case 'earcon':
                playEarcon(fx.kind)
                return
            case 'timer':
                view.startedAt = performance.now()
                view.now = view.startedAt
                return
            case 'holdTimer':
                if (holdTimer) clearTimeout(holdTimer)
                holdTimer = fx.op === 'start' ? setTimeout(() => dispatch({ type: 'HOLD_TIMEOUT' }), HOLD_LIMIT_MS) : null
                return
            case 'postCall':
                void postCall(fx.status)
                return
            case 'wakeLock':
                void keepAwake(fx.on)
                return
            case 'setSpeaker':
                return // needs native route control (phase 2); the button is hidden meanwhile
            case 'cleanup':
                cleanup()
                return
        }
    }

    // ── speech in ───────────────────────────────────────────────────────────

    const sttHandlers: SttHandlers = {
        ready: () => dispatch({ type: 'READY' }),
        speechStart: () => dispatch({ type: 'SPEECH_START' }),
        partial: (text) => {
            if (state.flags.muted || state.phase === 'speaking') return
            view.captions.user = text
            dispatch({ type: 'PARTIAL', text })
        },
        level: (l) => { micTarget = l },
        endOfTurn: () => dispatch({ type: 'END_OF_TURN' }),
        error: (kind: CallErrorKind) => {
            if (state.phase === 'connecting' && kind === 'permission') dispatch({ type: 'FAIL', kind })
            else dispatch({ type: 'STT_ERROR', kind })
        },
    }

    async function makeStt(): Promise<SttEngine | null> {
        if (devFake) return createFakeStt(sttHandlers)
        if (isNativePlatform() && Capacitor.isPluginAvailable('SpeechRecognition')) {
            return createNativeCallStt(sttHandlers, { contextualStrings: [assistantName.value, 'Lighthouse'] })
        }
        const { createWebCaptureStt, webCaptureSupported } = await import('./stt/webCapture')
        if (!webCaptureSupported()) return null
        return createWebCaptureStt(sttHandlers, { context: audioContext })
    }

    /**
     * The deferred restart (after a hold or a recognizer error). It runs after the transition's other
     * effects, so it checks the call again when it fires: muted (or speaking) means start, then pause at
     * once. The start is kept either way because the web engine needs it to get its stream back.
     */
    async function restartMic() {
        sttRetry = null
        const engineNow = stt
        if (!engineNow || !liveNow() || state.phase === 'onHold' || state.phase === 'connecting') return
        try {
            await engineNow.start()
        } catch {
            return
        }
        if (stt !== engineNow) return
        if (state.flags.muted || !HEARING.includes(state.phase)) {
            engineNow.pause()
            micTarget = 0
        }
    }

    async function openMic() {
        try {
            if (!devFake && isNativePlatform()) {
                const granted = await nativeSpeech().ensurePermission()
                if (state.phase !== 'connecting') return
                if (granted !== 'granted') {
                    dispatch({ type: 'FAIL', kind: 'permission' })
                    return
                }
            }
            await within(configReady, CONFIG_WAIT_MS, NO_CONFIG)
            if (state.phase !== 'connecting') return
            if (pipeline === 'realtime') {
                if (await openRealtime()) return
                // Couldn't connect: this call goes on the classic way (with the server talker if there is one).
                if (state.phase !== 'connecting') return
                const cfg = await within(configReady, 0, NO_CONFIG)
                pipeline = 'classic'
                setTalker(cfg.talker === 'server' ? 'server' : null)
            }
            if (!stt) stt = await makeStt()
            view.liveCaptions = stt?.liveCaptions ?? false
            if (!stt) {
                dispatch({ type: 'FAIL', kind: 'unavailable' })
                return
            }
            if (state.phase !== 'connecting') return
            await stt.start()
        } catch {
            // the engine reported it through `error`
        }
    }

    // ── speech out ──────────────────────────────────────────────────────────

    function makeTts(): TtsEngine {
        // Marks count only once this turn's own speech is queued (an earlier turn's tail doesn't).
        const ours = () => turn && !turn.done && (turn.marks.first_say !== undefined || turn.marks.first_text !== undefined)
        const handlers = {
            chunkStart: (text: string) => {
                view.captions.agent = text
                if (ours()) {
                    turn!.mark('playback')
                    postTiming()
                }
            },
            drained: () => dispatch({ type: 'TTS_DRAINED' }),
            requestStart: () => { if (ours()) turn!.mark('first_tts_request') },
            firstAudio: () => { if (ours()) turn!.mark('first_audio') },
        }
        if (devFake) return createFakeTts(handlers)
        return createStreamTts(handlers, { context: audioContext, sessionId: () => sessionId.value, ...(savedVoice.value ? { voice: savedVoice.value } : {}) })
    }

    function speak(text: string, runId: string | null, source: 'agent' | 'talker' = 'agent') {
        // Realtime calls are voiced by the voice model (the server relays the agent's words to it).
        if (!text || !liveNow() || state.phase === 'onHold' || pipeline === 'realtime') return
        if (turn && runId && runId === turn.runId) turn.mark(source === 'talker' ? 'first_say' : 'first_text')
        if (!tts) tts = makeTts()
        tts.enqueue(text, { runId })
        dispatch({ type: 'SPEAKABLE' })
    }

    // ── the conversation ────────────────────────────────────────────────────

    async function ensureSession(): Promise<string | null> {
        if (sessionId.value) return sessionId.value
        try {
            return await session.ensureSession()
        } catch {
            return null
        }
    }

    /**
     * Resolves true once the session's history is loaded (so what's already there stays silent), or false
     * when the wait is dropped (the call ended or another started first): the watcher never outlives it.
     */
    function whenHydrated(id: string): Promise<boolean> {
        stopHydrate?.()
        return new Promise((resolve) => {
            let stop: (() => void) | null = null
            const done = (ok: boolean) => {
                if (stopHydrate === drop) stopHydrate = null
                const s = stop
                if (s) queueMicrotask(() => s())
                resolve(ok)
            }
            const drop = () => done(false)
            stopHydrate = drop
            stop = watch(() => [st.sessionId, session.loading.value] as const, ([sid, loading]) => {
                if (sid === id && !loading) done(true)
            }, { immediate: true })
            // An immediate match resolved before `stop` existed.
            if (stopHydrate !== drop) stop()
        })
    }

    async function sendTurn(text: string, steer = false) {
        await sessionReady
        await within(configReady, CONFIG_WAIT_MS, NO_CONFIG)
        const t = turn
        t?.mark('send_start')
        const r = await session.send(text, { tier: 'quick', inputMode: 'voice', ...(steer ? { steer: true } : {}),
                                             ...(talkerMode ? { voiceTalker: talkerMode } : {}) })
        if (r) {
            if (t && !t.runId) {
                t.mark('sent')
                t.runId = r.runId || null
                t.steered = Boolean(r.steered || r.answered)
            }
            sent(r.runId)
            return
        }
        const info = session.errorInfo.value
        const busy = (info?.status === 422 || info?.status === 409) && Boolean(st.activeRunId)
        dispatch({ type: 'SEND_FAILED', kind: info?.code === 'usage_exhausted' ? 'usage' : busy ? 'busy' : 'network', text })
    }

    /**
     * A turn went out. Its run's events may already have arrived (even its end), so the machine is told
     * whether it still runs, and the speech tracker counts it active until an update says otherwise.
     */
    function sent(runId: string) {
        const run = runId ? st.runs[runId] : undefined
        const live = !run || run.status === 'queued' || run.status === 'running'
        if (runId) {
            speech?.track(runId, live)
            lastRunIds.push(runId)
        }
        dispatch({ type: 'SENT', runId, active: live })
    }

    async function postCall(status: 'started' | 'ended') {
        // The record is taken now: by the time the session id resolves, this call may be over (or another
        // may have started) and the shared state belongs to it.
        const cid = callId
        const body = status === 'started'
            ? { call_id: cid, status }
            : {
                call_id: cid, status,
                duration_ms: Math.min(86_400_000, Math.max(0, Math.round(view.startedAt ? (view.endedAt || performance.now()) - view.startedAt : 0))),
                turns: Math.min(10_000, state.turns),
                ...(state.endReason ? { reason: state.endReason } : {}),
            }
        const id = await sessionIdReady
        if (!id || !cid) return
        try {
            await agentApi().postCall(id, body)
        } catch {
            /* older backends don't record calls: the call itself is unaffected */
        }
    }

    function refreshCards() {
        const pending = pendingApprovals(st)
        view.approval = pending[pending.length - 1] ?? null
        const q = st.question
        view.question = q && !q.answered && (q.options.length > 0 || q.fields.length > 0) ? q : null
        let task: BrowserTaskCard | null = null
        for (let i = st.timeline.length - 1; i >= 0 && !task; i--) {
            const item = st.timeline[i]
            if (item.kind !== 'message') continue
            const m = st.messages[item.id]
            if (m?.role === 'user') break
            const card = m ? browserTaskFor(st, m) : null
            if (card && (card.status === 'proposed' || card.status === 'waiting_creator')) task = card
        }
        view.browserTask = task
        view.browserNeedsYou = st.browser.status === 'active' && (st.browser.controller === 'user' || st.browser.taskStatus === 'waiting_creator')
        const runId = lastRunIds[lastRunIds.length - 1] ?? st.activeRunId
        const plan = st.todo.find((t) => t.status === 'in_progress')?.title ?? null
        view.captions.progress = runId ? liveHeadline(turnSteps(toolsForRun(st, runId)), plan) : plan ?? 'Thinking'
    }

    watch(() => st.cursor, () => {
        if (!liveNow() || !speech || session.loading.value) return
        // A realtime turn the voice handed to the agent: its run is the next one the server started.
        if (pipeline === 'realtime' && turn && !turn.runId && turn.marks.delegated !== undefined && st.activeRunId) turn.runId = st.activeRunId
        speech.update(st)
        refreshCards()
    })
    watch(() => session.status.value, (s) => {
        if (liveNow()) dispatch({ type: 'RECONNECTING', on: s === 'reconnecting' })
    })

    // ── timers, levels, lifecycle ───────────────────────────────────────────

    function armIdle() {
        for (const t of idleTimers) clearTimeout(t)
        idleTimers = []
        if (state.phase !== 'listening' || state.runActive) return
        if (idlePrompted) {
            // Asked already: the call ends if the silence goes on after the prompt.
            idleTimers.push(setTimeout(() => dispatch({ type: 'IDLE_TIMEOUT' }), IDLE_END_MS - IDLE_PROMPT_MS))
            return
        }
        idleTimers.push(setTimeout(() => dispatch({ type: 'IDLE_PROMPT' }), IDLE_PROMPT_MS))
        idleTimers.push(setTimeout(() => dispatch({ type: 'IDLE_TIMEOUT' }), IDLE_END_MS))
    }

    function frame(t: number) {
        const dt = lastFrame ? Math.min(100, t - lastFrame) : 16
        lastFrame = t
        const hearing = !state.flags.muted && HEARING.includes(state.phase)
        if (rt) micTarget = Math.max(micTarget, rt.micLevel())
        levels.mic = smoothLevel(levels.mic, hearing ? micTarget : 0, dt)
        levels.out = smoothLevel(levels.out, rt ? rt.outLevel() : tts?.level() ?? 0, dt, 30, 140)
        // Native levels arrive ~15 times a second: let a stale peak fall away.
        micTarget *= Math.exp(-dt / 400)
        raf = requestAnimationFrame(frame)
    }

    function startLoops() {
        if (!clock) clock = setInterval(() => { view.now = performance.now() }, 1000)
        if (!raf && typeof requestAnimationFrame === 'function') raf = requestAnimationFrame(frame)
        offLifecycle?.()
        offLifecycle = onAppLifecycle({ pause: () => dispatch({ type: 'HOLD' }), resume: () => dispatch({ type: 'RESUME' }) }, { web: true })
    }

    function cleanup() {
        clearMicResume()
        postTiming()
        turn = null
        closeRealtime()
        stt?.stop()
        stt = null
        tts?.dispose()
        tts = null
        speech = null
        for (const t of idleTimers) clearTimeout(t)
        idleTimers = []
        idlePrompted = false
        stopHydrate?.()
        if (holdTimer) clearTimeout(holdTimer)
        if (sttRetry) clearTimeout(sttRetry)
        sttRetry = null
        holdTimer = null
        if (clock) clearInterval(clock)
        clock = null
        if (raf) cancelAnimationFrame(raf)
        raf = 0
        offLifecycle?.()
        offLifecycle = null
        levels.mic = 0
        levels.out = 0
        micTarget = 0
        Object.assign(view, {
            sessionId: null, callId: '', startedAt: 0, endedAt: 0, busy: null, approval: null, question: null, browserTask: null, browserNeedsYou: false,
            firstTurn: true, captions: { user: '', agent: '', progress: '' },
        })
        // Closes the call's own stream; the chat keeps its own.
        sessionId.value = null
        void keepAwake(false)
    }

    function setTalker(mode: VoiceTalker | null, sp: AgentSpeech | null = speech) {
        talkerMode = mode
        sp?.setTalker(mode === 'server')
    }

    // ── latency timeline ────────────────────────────────────────────────────

    /** A new spoken turn starts its timeline (the last one is posted first, if it got anywhere). */
    function newTurn(kind: 'classic' | 'realtime'): TurnTimeline {
        postTiming()
        turn = new TurnTimeline({ pipeline: kind, stt: kind === 'realtime' ? 'realtime' : stt?.kind,
                                  talker: talkerMode ?? 'off' })
        return turn
    }

    function postTiming() {
        const body = turn?.take()
        if (body) void agentApi().voiceTiming(body).catch(() => { /* older backends: no report */ })
    }

    // ── realtime ────────────────────────────────────────────────────────────

    async function openRealtime(): Promise<boolean> {
        const id = await sessionIdReady
        if (!id || state.phase !== 'connecting') return false
        try {
            const conn = await connectRealtime(id, {
                userSpeaking: (on) => {
                    if (on) view.captions.user = ''
                    else newTurn('realtime')
                    dispatch({ type: 'RT_USER', speaking: on })
                },
                userTranscript: (text) => {
                    view.captions.user = text
                    turn?.mark('transcript')
                },
                agentSpeaking: (on, relay) => {
                    if (on) turn?.mark(relay ? 'relay_audio' : 'talker_audio')
                    if (on && relay) postTiming()
                    dispatch({ type: 'RT_AGENT', speaking: on })
                },
                agentTranscript: (text) => { view.captions.agent = text },
                delegated: () => turn?.mark('delegated'),
                error: (kind) => {
                    if (!rt) return
                    rt = null
                    if (liveNow()) dispatch({ type: 'FAIL', kind })
                },
            }, { context: audioContext })
            if (state.phase !== 'connecting') {
                conn.close()
                return false
            }
            rt = conn
            view.liveCaptions = true
            dispatch({ type: 'READY' })
            rt.setMuted(state.flags.muted)
            return true
        } catch {
            return false
        }
    }

    function closeRealtime() {
        const conn = rt
        rt = null
        conn?.close()
    }

    // The status bar reads light over the dark call screen.
    watch(() => liveNow() && !state.flags.minimized, (on) => void callStatusBar(on))

    // ── the API ─────────────────────────────────────────────────────────────

    function start(o: StartCallOptions) {
        if (lingerTimer) clearTimeout(lingerTimer)
        lingerTimer = null
        if (state.phase === 'ended' || state.phase === 'error') cleanup()
        opts = o
        callId = newCallId()
        view.callId = callId
        view.endedAt = 0
        lastRunIds.length = 0
        view.captions = { user: '', agent: '', progress: '' }
        view.firstTurn = true
        idlePrompted = false
        try {
            view.disclose = localStorage.getItem(DISCLOSED_KEY) !== '1'
            localStorage.setItem(DISCLOSED_KEY, '1')
        } catch {
            view.disclose = true
        }
        sessionOpts.surface = o.surface
        sessionOpts.entityRefs = o.entityRefs
        sessionOpts.title = o.title
        sessionOpts.onSessionCreated = (id) => {
            view.sessionId = id
            o.onSessionCreated?.(id)
        }
        if (o.sessionId) {
            sessionId.value = o.sessionId
            view.sessionId = o.sessionId
        }
        // Speech starts listening to the session only once its history is loaded (what's there stays silent).
        speech = null
        pipeline = 'classic'
        talkerMode = null
        const sp = new AgentSpeech({
            speak: (text, runId, source) => speak(text, runId, source),
            runActivity: (active) => dispatch({ type: active ? 'RUN_ACTIVE' : 'RUN_IDLE' }),
            approval: (pending) => dispatch({ type: pending ? 'APPROVAL_NEEDED' : 'APPROVAL_RESOLVED' }),
        })
        const gen = ++calls
        stopHydrate?.()
        // The server's config picks this call's pipeline and talker before the mic opens (openMic waits for it).
        configReady = loadVoiceConfig().then((cfg) => {
            if (gen !== calls) return cfg
            pipeline = cfg.realtime && realtimeSupported() && !devFake ? 'realtime' : 'classic'
            setTalker(pipeline === 'realtime' ? 'realtime' : cfg.talker === 'server' ? 'server' : null, sp)
            dispatch({ type: 'CONFIG', steer: cfg.steer })
            return cfg
        })
        sessionIdReady = ensureSession()
        sessionReady = (async () => {
            const id = await sessionIdReady
            if (!id || gen !== calls) return id
            view.sessionId = id
            if (!(await whenHydrated(id))) return id
            if (gen === calls && liveNow()) {
                sp.begin(st)
                speech = sp
                refreshCards()
            }
            return id
        })()
        startLoops()
        dispatch({ type: 'START' })
    }

    async function approve(id: string, action: 'approve' | 'reject') {
        view.busy = id
        const cs = await session.actOnChangeset(id, action)
        view.busy = null
        if (cs) void notifyHaptic(action === 'approve' ? 'success' : 'warning')
        else void notifyHaptic('error')
        speech?.update(st)
        refreshCards()
    }

    async function answer(text: string) {
        view.busy = 'answer'
        const r = await session.send(text, { tier: 'quick' })
        view.busy = null
        if (r && r.runId) sent(r.runId)
        if (!r) {
            // The question stays: the card shows its choices again.
            view.answerFailed++
            void notifyHaptic('error')
        }
        refreshCards()
    }

    function openChat() {
        const id = view.sessionId
        dispatch({ type: 'MINIMIZE' })
        if (!id) return
        // The host that started the call can show it only while that conversation is still mounted there
        // (it may have moved on, or unmounted with its callback): otherwise route to the conversation.
        if (opts.openChat && isSessionMounted(id)) opts.openChat(id)
        else void router?.push(`/dashboard/conversations/${id}`)
    }

    engine = { start, dispatch, approve, answer, openChat, say: (t) => speak(t, null) }
    hostReady.value = true

    if (import.meta.env.DEV && typeof window !== 'undefined') {
        // window.__voice: drive any call state from the console / Playwright (dev builds only).
        ;(window as unknown as { __voice: unknown }).__voice = {
            fake: (on = true) => useFakeVoiceEngines(on),
            start: (o?: StartCallOptions) => voiceCall.start(o),
            /** The creator says this (fake STT). */
            say: (text: string) => (stt as FakeStt | null)?.say?.(text),
            /** Lighthouse says this (local line). */
            speak: (text: string) => speak(text, null),
            dispatch,
            state: () => ({ ...state, flags: { ...state.flags }, captions: { ...view.captions }, sessionId: view.sessionId }),
            caption: (c: Partial<CallCaptions>) => Object.assign(view.captions, c),
            view,
            end: () => voiceCall.end(),
        }
    }

    return {
        owner: true,
        dispose() {
            if (liveNow()) dispatch({ type: 'END', reason: 'user' })
            cleanup()
            engine = null
            hostReady.value = false
        },
    }
}
